<template>
  <div class="signin">
    <!-- Better Auth sign-in, email first (plan Task 11, sign-in redesign).
         Colours come only from the tenant theme (`color="primary"`, the --sh-*
         tokens). The parent exchanges the session for a bridge token.
         Plain elements, not <template> fragments: a switch between fragment
         branches broke the DOM patch (Vue "removeFragment", seen in tests). -->
    <div v-if="step === 'email'" class="signin__body">
      <ProviderButtons v-if="socialProviders.length" :providers="socialProviders" :busy="busy" @pick="startSocial" />

      <p v-if="socialProviders.length && usesEmail" class="signin__or">{{ t("auth.identity.orWithEmail") }}</p>

      <form v-if="usesEmail" class="signin__body" data-test="email-form" @submit.prevent="onContinue">
        <IconField
          id="si-email"
          v-model="email"
          :label="t('auth.identity.email')" :placeholder="t('auth.identity.emailPlaceholder')"
          type="email"
          icon="mail"
          autocomplete="email"
          inputmode="email"
          :disabled="!!busy"
        />
        <v-btn
          class="signin__primary"
          :data-test="showPassword ? 'continue' : 'provider-magicLink'"
          color="primary"
          height="52"
          block
          :loading="busy === 'magic' && !showPassword"
          :disabled="!!busy"
          @click="onContinue"
        >
          {{ showPassword ? t("auth.identity.continue") : t("auth.identity.sendLink") }}
          <svg v-if="showPassword" class="signin__arrow" viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
        </v-btn>
      </form>

      <button
        v-if="showPassword && showMagicLink"
        type="button"
        class="signin__text-link"
        data-test="provider-magicLink"
        :disabled="!!busy"
        @click="sendMagicLink"
      >
        {{ busy === "magic" ? t("auth.signingIn") : t("auth.identity.sendLinkInstead") }}
      </button>

      <p v-if="allowSignUp && showPassword" class="signin__switch">
        {{ t("auth.identity.noAccount") }}
        <button type="button" class="signin__text-link" @click="openStep('sign-up')">{{ t("auth.identity.createAccount") }}</button>
      </p>
    </div>

    <form v-else-if="step === 'password'" class="signin__body" data-test="password-form" @submit.prevent="submitPassword">
      <EmailChip :email="email" @change="openStep('email')" />
      <IconField
        id="si-password"
        v-model="password"
        :label="t('auth.identity.password')"
        type="password"
        icon="lock"
        autocomplete="current-password"
        autofocus
        :disabled="!!busy"
      >
        <template #label-action>
          <button type="button" class="signin__text-link signin__text-link--small" data-test="forgot-password" @click="openStep('forgot')">
            {{ t("auth.identity.forgotPassword") }}
          </button>
        </template>
      </IconField>
      <v-btn class="signin__primary" type="submit" color="primary" height="52" block :loading="busy === 'password'">{{ submitLabel }}</v-btn>
    </form>

    <LinkSentCard v-else-if="step === 'link-sent'" :email="email" @back="openStep('email')" />

    <form v-else-if="step === 'sign-up'" class="signin__body" data-test="sign-up-form" @submit.prevent="submitPassword">
      <IconField id="si-name" v-model="name" :label="t('auth.identity.name')" icon="user" autocomplete="name" autofocus :disabled="!!busy" />
      <IconField id="si-email" v-model="email" :label="t('auth.identity.email')" :placeholder="t('auth.identity.emailPlaceholder')" type="email" icon="mail" autocomplete="email" :disabled="!!busy" />
      <IconField id="si-password" v-model="password" :label="t('auth.identity.password')" type="password" icon="lock" autocomplete="new-password" :disabled="!!busy" />
      <v-btn class="signin__primary" type="submit" color="primary" height="52" block :loading="busy === 'password'">{{ t("auth.identity.signUp") }}</v-btn>
    </form>

    <form v-else-if="step === 'forgot'" class="signin__body" data-test="forgot-form" @submit.prevent="sendResetLink">
      <p class="signin__hint">{{ t("auth.identity.forgotIntro") }}</p>
      <IconField id="si-email" v-model="email" :label="t('auth.identity.email')" :placeholder="t('auth.identity.emailPlaceholder')" type="email" icon="mail" autocomplete="email" autofocus :disabled="!!busy" />
      <v-btn class="signin__primary" type="submit" color="primary" height="52" block :loading="busy === 'reset'">{{ t("auth.identity.sendResetLink") }}</v-btn>
    </form>

    <form v-else class="signin__body" data-test="two-factor-form" @submit.prevent="verifyCode">
      <p class="signin__title">{{ t("auth.identity.twoFactorTitle") }}</p>
      <p class="signin__hint">{{ useBackupCode ? t("auth.identity.backupHint") : t("auth.identity.twoFactorHint") }}</p>
      <IconField
        id="si-code"
        v-model="code"
        :label="t('auth.identity.code')"
        icon="code"
        :autocomplete="useBackupCode ? 'off' : 'one-time-code'"
        :inputmode="useBackupCode ? 'text' : 'numeric'"
        autofocus
        :disabled="!!busy"
      />
      <v-btn class="signin__primary" type="submit" color="primary" height="52" block :loading="busy === 'code'">{{ t("auth.identity.verify") }}</v-btn>
      <button type="button" class="signin__text-link" :disabled="!!busy" @click="toggleBackupCode">
        {{ useBackupCode ? t("auth.identity.useAuthenticator") : t("auth.identity.useBackupCode") }}
      </button>
    </form>

    <p v-if="notice" class="signin__notice" role="status" data-test="notice">
      <v-icon size="15">info_outline</v-icon>
      <span>{{ notice }}</span>
    </p>
    <v-btn v-if="needsVerification" data-test="resend-verification" variant="outlined" height="48" block :loading="busy === 'resend'" @click="resendVerification">
      {{ t("auth.identity.resendLink") }}
    </v-btn>
    <p v-if="errorText" class="sh-card__error signin__error" role="alert">
      <v-icon size="15">error_outline</v-icon>
      <span>{{ errorText }}</span>
    </p>

    <button v-if="showBack" type="button" class="signin__text-link" :disabled="!!busy" @click="openStep('email')">
      {{ t("auth.identity.back") }}
    </button>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, type PropType } from "vue";
import { useI18n } from "vue-i18n";
import {
  ALL_IDENTITY_PROVIDERS,
  authClient,
  identityReturnUrl,
  resetPasswordUrl,
  sendVerificationLink,
  signInWithProvider,
  SOCIAL_PROVIDERS,
  toIdentityError,
  type IdentityError,
  type IdentityProvider,
  type SocialProvider,
} from "@/auth/identity";
import { humaniseIdentityError } from "@/auth/authErrors";
import IconField from "@/shell/signin/IconField.vue";
import ProviderButtons from "@/shell/signin/ProviderButtons.vue";
import LinkSentCard from "@/shell/signin/LinkSentCard.vue";
import EmailChip from "@/shell/signin/EmailChip.vue";

type Step = "email" | "password" | "link-sent" | "sign-up" | "forgot" | "two-factor";
type Busy = "password" | "magic" | "reset" | "resend" | "code" | SocialProvider;

export default defineComponent({
  name: "SignInForm",
  components: { IconField, ProviderButtons, LinkSentCard, EmailChip },
  props: {
    /** The methods this tenant shows (`loginPage.providers`). */
    providers: {
      type: Array as PropType<IdentityProvider[]>,
      default: () => [...ALL_IDENTITY_PROVIDERS],
    },
    /** The password button text; empty means the default "Sign in". */
    signInLabel: { type: String, default: "" },
    /** Show "Create an account" (apps that take self-serve sign-ups). */
    allowSignUp: { type: Boolean, default: false },
  },
  emits: {
    "signed-in": () => true,
    error: (payload: { code: string; message: string }) => !!payload,
  },
  setup(props, { emit }) {
    const { t } = useI18n();
    const step = ref<Step>("email");
    const email = ref("");
    const password = ref("");
    const name = ref("");
    const code = ref("");
    const useBackupCode = ref(false);
    const busy = ref<Busy | null>(null);
    const notice = ref<string | null>(null);
    const errorText = ref<string | null>(null);
    const needsVerification = ref(false);

    const showPassword = computed(() => props.providers.includes("password"));
    const showMagicLink = computed(() => props.providers.includes("magicLink"));
    const usesEmail = computed(() => showPassword.value || showMagicLink.value);
    const socialProviders = computed(() => SOCIAL_PROVIDERS.filter((p) => props.providers.includes(p)));
    const submitLabel = computed(() => props.signInLabel.trim() || t("auth.signIn"));
    // The password step has its own way back (the email chip), and the sent card has "another way".
    const showBack = computed(() => ["sign-up", "forgot", "two-factor"].includes(step.value));

    function clearMessages(): void {
      notice.value = null;
      errorText.value = null;
      needsVerification.value = false;
    }

    function openStep(next: Step): void {
      clearMessages();
      if (next === "email") password.value = "";
      step.value = next;
      code.value = "";
      useBackupCode.value = false;
    }

    function showError(error: IdentityError): void {
      if (error.code === "EMAIL_NOT_VERIFIED") {
        needsVerification.value = true;
        notice.value = t("auth.identity.unverified");
      } else {
        errorText.value = humaniseIdentityError(error);
      }
      emit("error", { code: error.code, message: error.message });
    }

    /** One guard for every action: clear old messages, check the email, mark the button busy. */
    async function run(kind: Busy, needsEmail: boolean, action: () => Promise<void>): Promise<void> {
      if (busy.value) return;
      clearMessages();
      if (needsEmail && !email.value.trim()) {
        errorText.value = t("auth.identity.emailRequired");
        return;
      }
      busy.value = kind;
      try {
        await action();
      } catch (e) {
        showError({ code: "NETWORK", message: e instanceof Error ? e.message : String(e), status: 0 });
      } finally {
        busy.value = null;
      }
    }

    /** Email first: Continue asks for the password, or sends the link when that is the only method. */
    function onContinue(): void {
      if (!showPassword.value) {
        void sendMagicLink();
        return;
      }
      clearMessages();
      if (!email.value.trim()) {
        errorText.value = t("auth.identity.emailRequired");
        return;
      }
      openStep("password");
    }

    const submitPassword = () =>
      run("password", true, async () => {
        if (!password.value) {
          errorText.value = t("auth.identity.passwordRequired");
          return;
        }
        const address = email.value.trim();
        if (step.value === "sign-up") {
          const { error } = await authClient.signUp.email({
            name: name.value.trim() || address,
            email: address,
            password: password.value,
            callbackURL: identityReturnUrl(),
          });
          if (error) return showError(toIdentityError(error));
          openStep("email");
          notice.value = t("auth.identity.signUpSent", { email: address });
          return;
        }
        const { data, error } = await authClient.signIn.email({ email: address, password: password.value });
        if (error) return showError(toIdentityError(error));
        if ((data as { twoFactorRedirect?: boolean } | null)?.twoFactorRedirect) {
          openStep("two-factor");
          return;
        }
        emit("signed-in");
      });

    const sendMagicLink = () =>
      run("magic", true, async () => {
        const { error } = await authClient.signIn.magicLink({ email: email.value.trim(), callbackURL: identityReturnUrl() });
        if (error) return showError(toIdentityError(error));
        openStep("link-sent");
      });

    const sendResetLink = () =>
      run("reset", true, async () => {
        const address = email.value.trim();
        const { error } = await authClient.requestPasswordReset({ email: address, redirectTo: resetPasswordUrl() });
        if (error) return showError(toIdentityError(error));
        openStep("email");
        notice.value = t("auth.identity.resetSent", { email: address });
      });

    const resendVerification = () =>
      run("resend", true, async () => {
        const address = email.value.trim();
        const error = await sendVerificationLink(address);
        if (error) return showError(error);
        notice.value = t("auth.identity.verificationSent", { email: address });
      });

    const startSocial = (provider: SocialProvider) =>
      run(provider, false, async () => {
        const error = await signInWithProvider(provider, `${window.location.pathname}${window.location.search}`);
        if (error) showError(error);
      });

    const verifyCode = () =>
      run("code", false, async () => {
        const value = code.value.replace(/\s+/g, "");
        if (!value) {
          errorText.value = humaniseIdentityError({ code: "INVALID_CODE" });
          return;
        }
        const { error } = useBackupCode.value
          ? await authClient.twoFactor.verifyBackupCode({ code: value })
          : await authClient.twoFactor.verifyTotp({ code: value });
        if (error) return showError(toIdentityError(error));
        emit("signed-in");
      });

    function toggleBackupCode(): void {
      clearMessages();
      code.value = "";
      useBackupCode.value = !useBackupCode.value;
    }

    return {
      t,
      step,
      email,
      password,
      name,
      code,
      useBackupCode,
      busy,
      notice,
      errorText,
      needsVerification,
      showPassword,
      showMagicLink,
      usesEmail,
      socialProviders,
      submitLabel,
      showBack,
      openStep,
      onContinue,
      submitPassword,
      sendMagicLink,
      sendResetLink,
      resendVerification,
      startSocial,
      verifyCode,
      toggleBackupCode,
    };
  },
});
</script>

<style scoped>
.signin,
.signin__body {
  display: flex;
  flex-direction: column;
  gap: 18px;
}
.signin__primary {
  border-radius: 12px;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0;
  text-transform: none;
  box-shadow: 0 8px 24px rgba(var(--v-theme-primary), 0.28);
}
.signin__arrow {
  width: 18px;
  height: 18px;
  margin-left: 8px;
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
}
.signin__or {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0;
  font-family: var(--si-mono, ui-monospace, SFMono-Regular, Menlo, monospace);
  font-size: 11px;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: var(--sh-text-60);
}
.signin__or::before,
.signin__or::after {
  content: "";
  flex: 1;
  border-top: 1px solid var(--sh-hairline);
}
.signin__text-link {
  align-self: center;
  min-height: 44px;
  padding: 6px;
  border: 0;
  background: transparent;
  font: inherit;
  font-size: 14px;
  font-weight: 500;
  color: rgb(var(--v-theme-primary));
  cursor: pointer;
}
.signin__text-link--small {
  min-height: 0;
  padding: 0;
  font-size: 13px;
}
.signin__text-link:hover {
  text-decoration: underline;
}
.signin__text-link:disabled {
  opacity: 0.6;
  cursor: default;
}
.signin__text-link:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 2px;
}
.signin__hint {
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  color: var(--sh-text-60);
  overflow-wrap: anywhere;
}
.signin__title {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
}
.signin__notice {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin: 0;
  font-size: 13px;
  line-height: 1.4;
  color: var(--sh-text-60);
}
.signin__error {
  margin: 0;
}
.signin__switch {
  margin: 0;
  text-align: center;
  font-size: 13px;
  color: var(--sh-text-60);
}
</style>
